/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import type {
    MonitorandprotectAdsreportCreateParams,
    MonitorAndProtectModelAdsApiModelAdTableRow,
} from "@similarweb/pro-api-client";

import { apiClient, useApiMutation } from "app/modules/api-client";

import { useFilterFactoryStateFromParams } from "../../features/ads-report/filter-factory/hooks";
import { DEFAULT_MAX_EXCEL_ROWS } from "../../shared/data/component-config/component-config";
import { useMonitorAndProtectDataAndParams } from "../../shared/hooks/navigations";
import { parseDurationToDateRange } from "../date-range-utils";

import { useCurrentActiveCampaignLocationCountries, useMonitorAndProtectCurrentCampaign } from "./campaign";

export const MONITOR_AND_PROTECT_ADS_REPORT_KEY = ["monitorAndProtect", "adsReport"];
export const MONITOR_AND_PROTECT_AD_KEYWORDS_KEY = ["monitorAndProtect", "adKeywords"];

export function useMonitorAndProtectAds(data: Partial<MonitorandprotectAdsreportCreateParams> = {}) {
    const {
        data: campaignData,
        error: campaignError,
        isLoading: isLoadingCampaign,
    } = useMonitorAndProtectCurrentCampaign();
    const activeLocations = useCurrentActiveCampaignLocationCountries();
    const { device, sortBy, sortDir, duration } = useMonitorAndProtectDataAndParams();
    const { apiRequestFilters } = useFilterFactoryStateFromParams();

    const dateRange = useMemo(() => {
        const { from, to } = parseDurationToDateRange(duration);
        return { isDaily: true, from, to };
    }, [duration]);

    // added because Tracking requires to be domain instead advertiser (BE has advertiser)
    const adjustedSort = sortBy === "domain" ? "advertiser" : sortBy;

    const queryParams = {
        campaignGuid: campaignData?.id || "",
        asc: sortDir === "ascend",
        sort: adjustedSort,
        isDaily: dateRange.isDaily,
        from: dateRange.from,
        to: dateRange.to,
        ...data,
    };

    const bodyParams = {
        countries: activeLocations,
        device: device,
        ...apiRequestFilters,
    };

    return useQuery({
        queryKey: [
            ...MONITOR_AND_PROTECT_ADS_REPORT_KEY,
            campaignData?.id,
            sortDir,
            sortBy,
            dateRange.isDaily,
            dateRange.from,
            dateRange.to,
            activeLocations,
            device,
            apiRequestFilters,
            data.page,
        ],
        queryFn: () => apiClient.MonitorAndProtectMaster.monitorandprotectAdsreportCreate(queryParams, bodyParams),
        enabled: !isLoadingCampaign && !campaignError && !!campaignData,
    });
}

export function useMonitorAndProtectAdKeywords(adId: MonitorAndProtectModelAdsApiModelAdTableRow["adId"]) {
    const {
        data: campaignData,
        error: campaignError,
        isLoading: isLoadingCampaign,
    } = useMonitorAndProtectCurrentCampaign();
    const activeLocations = useCurrentActiveCampaignLocationCountries();
    const { device, duration } = useMonitorAndProtectDataAndParams();

    const dateRange = useMemo(() => {
        const { from, to } = parseDurationToDateRange(duration);
        return { isDaily: true, from, to };
    }, [duration]);

    const queryParams = {
        campaignGuid: campaignData?.id || "",
        adId,
        isDaily: dateRange.isDaily,
        from: dateRange.from,
        to: dateRange.to,
    };

    const bodyParams = {
        countries: activeLocations,
        device: device,
    };

    return useQuery({
        queryKey: [
            ...MONITOR_AND_PROTECT_AD_KEYWORDS_KEY,
            campaignData?.id,
            adId,
            activeLocations,
            device,
            dateRange.isDaily,
            dateRange.from,
            dateRange.to,
        ],
        queryFn: () =>
            apiClient.MonitorAndProtectMaster.monitorandprotectAdsreportAdsclicksbreakdownCreate(
                queryParams,
                bodyParams,
            ),
        enabled: !isLoadingCampaign && !campaignError && !!campaignData,
    });
}

export function useMonitorAndProtectAdExcel() {
    const {
        data: campaignData,
        error: campaignError,
        isLoading: isLoadingCampaign,
    } = useMonitorAndProtectCurrentCampaign();

    const activeLocations = useCurrentActiveCampaignLocationCountries();
    const { device, sortBy, sortDir, duration } = useMonitorAndProtectDataAndParams();
    const { apiRequestFilters } = useFilterFactoryStateFromParams();

    const dateRange = useMemo(() => {
        const { from, to } = parseDurationToDateRange(duration);
        return { isDaily: true, from, to };
    }, [duration]);

    const isEnabled = !isLoadingCampaign && !campaignError && !!campaignData;

    const { mutateAsync, isPending } = useApiMutation(
        "MonitorAndProtectMaster",
        "monitorandprotectAdsreportExcelCreate",
    );

    const getExcel = useCallback(
        (rowCount: number = DEFAULT_MAX_EXCEL_ROWS) => {
            if (!isEnabled) {
                throw new Error("Cannot fetch excel report, dependencies not met");
            }

            return mutateAsync([
                {
                    campaignGuid: campaignData.id,
                    sort: sortBy,
                    asc: sortDir === "ascend",
                    isDaily: dateRange.isDaily,
                    from: dateRange.from,
                    to: dateRange.to,
                },
                {
                    countries: activeLocations,
                    device: device,
                    ...apiRequestFilters,
                    maxRows: rowCount,
                },
                {
                    format: "blob",
                    headers: {
                        Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    },
                },
            ]);
        },
        [
            isEnabled,
            mutateAsync,
            campaignData.id,
            sortBy,
            sortDir,
            dateRange,
            activeLocations,
            device,
            apiRequestFilters,
        ],
    );

    return { getExcel, isLoading: isPending };
}

export function useMonitorAndProtectAdExcelDownload() {
    const { getExcel, isLoading } = useMonitorAndProtectAdExcel();

    const downloadExcel = useCallback(
        async (rowCount?: number) => {
            await getExcel(rowCount)
                .then((response: Blob) => {
                    const url = window.URL.createObjectURL(response);
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", "ads_report.xlsx");
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode?.removeChild(link);
                })
                .catch((error) => {
                    console.error("Error downloading the Excel report:", error);
                    throw error;
                });
        },
        [getExcel],
    );

    return { downloadExcel, isLoading };
}
